import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { NetworkError, ValidationError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import { INCIDENCE_TYPES, LEAVE_CHANGE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import PayrollIntegration, {
  PayrollIntegrationType,
} from 'modules/payroll_integrations/lib/catalog'
import IncidenceSync, {
  Status,
  Sync,
} from 'modules/payroll_integrations_datev_lauds/lib/IncidenceSync/store'
import OauthService, {
  Status as AuthStatus,
} from 'modules/payroll_integrations_datev_lauds/lib/OauthService'
import CompanyInstallationsChecker from 'modules/payroll_integrations_kombo/lib/CompanyInstallations/checker'

import Absences from './Absences'
import MissingSalaryCode from './Banners/MissingSalaryCode'
import Information from './Banners/information'
import Errored from './Errored'
import Hiring from './Hiring'
import NotAuthenticated from './NotAuthenticated'
import PersonalData from './PersonalData'
import Termination from './Termination'

interface Props {
  incidenceType: keyof typeof INCIDENCE_TYPES
  incidenceDetail: IncidenceDetail
  companyId?: string
}

const selectComponent = (type: keyof typeof INCIDENCE_TYPES | null) => {
  switch (type) {
    case INCIDENCE_TYPES.hiring:
      return Hiring
    case INCIDENCE_TYPES.contract:
    case INCIDENCE_TYPES.name:
    case INCIDENCE_TYPES.address:
    case INCIDENCE_TYPES.email:
    case INCIDENCE_TYPES.phone_number:
    case INCIDENCE_TYPES.bank:
    case INCIDENCE_TYPES.team:
    case INCIDENCE_TYPES.health_insurance:
      return PersonalData
    case INCIDENCE_TYPES.termination:
      return Termination
    case INCIDENCE_TYPES.sick:
    case INCIDENCE_TYPES.parental:
    case INCIDENCE_TYPES.custom_leave:
      return Absences
    default:
      return null
  }
}

const Incidence = ({ incidenceType, incidenceDetail, companyId }: Props) => {
  const incidenceId = incidenceDetail.attributes.incidenceId
  const Component = selectComponent(incidenceType)
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.NotAvailable)
  const [status, setStatus] = useState<Sync | null>(null)
  const [error, setError] = useState<string | null>(null)
  const isBookkeeperView = useBookkeeperView()
  const [datevLaudsInstalled, setDatevLaudsInstalled] = useState<boolean>(false)
  const [payrollIntegration, setPayrollIntegration] = useState<PayrollIntegrationType | null>(null)
  const isHiringIncidence = incidenceType === INCIDENCE_TYPES.hiring
  const isUserAuthenticated = true
  const [pendingState, setPendingState] = useLocalStorage<boolean>(
    `factorial:setPendingState_${incidenceId}`,
    false
  )
  const isLeaveIncidence = Object.values(LEAVE_CHANGE_TYPES).includes(incidenceType)

  const getOauthStatus = async () => {
    setAuthStatus(await OauthService.getStatus({ companyId: companyId }, isBookkeeperView))
  }

  const { loading } = useAsyncData(async () => {
    const isDatevLaudsInstalled = await CompanyInstallationsChecker.isDatevLaudsInstalled()

    if (!isDatevLaudsInstalled) return
    setDatevLaudsInstalled(isDatevLaudsInstalled)
    await getOauthStatus()
  }, [companyId])

  const { running: checkingStatus, performAction: checkStatus } = useAsyncAction(
    async () => {
      if (!isHiringIncidence && !isUserAuthenticated) return
      if (payrollIntegration === null) return
      if (isLeaveIncidence && !incidenceDetail.id) return

      const incidenceStatus = await IncidenceSync.status({
        companyId,
        incidenceId,
        integration: payrollIntegration,
        isBookkeeperView: isBookkeeperView,
      })
      setStatus(incidenceStatus)
      if (pendingState === false && incidenceStatus?.status === Status.Pending)
        setPendingState(true)
    },
    {
      successFlash: false,
    }
  )

  useUnsafeEffect(() => {
    if (!datevLaudsInstalled) return

    setPayrollIntegration(PayrollIntegration.datevLauds)

    if (Component === null) return

    checkStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    datevLaudsInstalled,
    payrollIntegration,
    companyId,
    incidenceId,
    isBookkeeperView,
    authStatus,
    isHiringIncidence,
    isUserAuthenticated,
  ])

  const { running: syncing, performAction: onSync } = useAsyncAction(
    async () => {
      try {
        if (payrollIntegration === null) return

        const sync = await IncidenceSync.sync({
          companyId,
          incidenceId: incidenceDetail.attributes.incidenceId,
          integration: payrollIntegration,
          isBookkeeperView: isBookkeeperView,
        })

        setStatus(sync)

        setError(null)
      } catch (e: unknown) {
        if (e instanceof NetworkError) {
          return
        }

        let message: string
        if (e instanceof ValidationError) {
          message = JSON.stringify({ error_code: e.message })
        } else if (e instanceof Error) {
          message = JSON.stringify({ error_code: e.message })
        } else {
          message = JSON.stringify(error)
        }

        setError(message)
        throw e
      }
    },
    {
      successMessage: i18n.t('payroll_integrations.incidences.datev_lauds.sync.success_message'),
    }
  )

  const show = !loading && payrollIntegration !== null && datevLaudsInstalled && Component !== null

  if (!show) return <></>

  if (authStatus != AuthStatus.Authenticated && status?.status !== Status.Done)
    return <NotAuthenticated />

  if (incidenceType === INCIDENCE_TYPES.contract && status?.syncable_fields?.length === 0)
    return null

  if (
    incidenceType === INCIDENCE_TYPES.team &&
    incidenceDetail.attributes.teams.split(', ').length > 1 && // the employee can be in multiple teams and this was concantenated and stored as a strin in the DB
    status?.status !== Status.Done
  )
    return <Information />

  if (error !== null)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Errored error={error} syncing={syncing} onSync={onSync} />
      </Box>
    )

  if (isLeaveIncidence && !incidenceDetail.id) return null

  // For Hiring incidences we want to show the status of an incidence even before the user being logged in
  if (status?.status === Status.Unavailable && !isUserAuthenticated && isHiringIncidence)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Component
          sync={status}
          syncing={syncing}
          onSync={onSync}
          onStatusCheck={checkStatus}
          checkingStatus={checkingStatus}
          pendingState={pendingState ?? false}
          setPendingState={setPendingState}
          incidenceType={incidenceType}
        />
      </Box>
    )

  return (
    status && (
      <>
        {['missing_salary_code', 'missing_legal_entity_mapping', 'missing_leave_type_mapping'].some(
          (field) => status?.unavailable_fields?.includes(field)
        ) && (
          <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40" gap="s8">
            <MissingSalaryCode
              unavailable_fields={status.unavailable_fields}
              isLeaveIncidence={isLeaveIncidence}
            />
          </Box>
        )}
        <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
          <Component
            sync={status}
            syncing={syncing}
            onSync={onSync}
            onStatusCheck={checkStatus}
            checkingStatus={checkingStatus}
            pendingState={pendingState ?? false}
            setPendingState={setPendingState}
            incidenceType={incidenceType}
          />
        </Box>
      </>
    )
  )
}

export default Incidence
